"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, ShieldCheck } from "lucide-react";
export default function JoinInsuranceCollective() {
  return <section id="gå-med-i-försäkringskollektivet" className="dark bg-background text-foreground py-24 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <ShieldCheck className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-4">
            Starkare Tillsammans
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
            Om vi går samman kan vi förhandla fram bättre och billigare försäkringsvillkor för våra Tesla-bilar. Anmäl ditt intresse nedan för att gå med i försäkringskollektivet.
          </p>
        </div>

        <FormProvider formId="insurance_collective_form_cheaper_insurance" sectionName="join-insurance-collective" defaultValues={{
        full_name: "",
        email: "",
        town: "",
        vehicle_model: "",
        phone: ""
      }} validationRules={{
        full_name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        town: {
          required: true
        },
        vehicle_model: {
          required: true
        },
        phone: {
          pattern: "phone"
        }
      }} submitText="Gå med i kollektivet" successMessage="Tack för din anmälan! Vi hör av oss så snart vi har uppdateringar om försäkringserbjudanden.">
          <InsuranceForm />
        </FormProvider>
      </div>
    </section>;
}
function InsuranceForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="rounded-[3px] border-border/50 shadow-lg bg-card">
        <CardContent className="pt-12 pb-12 text-center flex flex-col items-center">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-6">
            <ShieldCheck className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-2xl font-serif font-bold text-foreground mb-2">Välkommen till kollektivet!</h3>
          <p className="text-muted-foreground mb-8 max-w-md">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="rounded-[3px]">
            Registrera en till bil
          </Button>
        </CardContent>
      </Card>;
  }
  const vehicleOptions = ["Model S", "Model 3", "Model X", "Model Y", "Annat"];
  return <Card className="rounded-[3px] border-border/50 shadow-lg bg-card">
      <CardHeader className="pb-6 border-b border-border/50">
        <CardTitle className="text-2xl font-serif text-foreground">Gå med i försäkringskollektivet</CardTitle>
        <CardDescription className="font-sans text-muted-foreground">
          Fyll i dina uppgifter för att visa intresse. Det är helt kostnadsfritt och ej bindande.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <Form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <FormField control={control} name="full_name" rules={{
            required: "Fullständigt namn är obligatoriskt"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Fullständigt namn</FormLabel>
                  <FormControl>
                    <Input placeholder="Anna Svensson" {...field} className="rounded-[3px]" />
                  </FormControl>
                  <FormDescription>Ange ditt för- och efternamn.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "E-postadress är obligatorisk",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Ogiltig e-postadress"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>E‑postadress</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="anna@example.com" {...field} className="rounded-[3px]" />
                  </FormControl>
                  <FormDescription>Vi skickar bekräftelse och vidare information till denna adress.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="town" rules={{
            required: "Ort är obligatoriskt"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Ort</FormLabel>
                  <FormControl>
                    <Input placeholder="Stockholm" {...field} className="rounded-[3px]" />
                  </FormControl>
                  <FormDescription>Stad eller ort där du är bosatt.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="vehicle_model" rules={{
            required: "Du måste välja en modell"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Tesla‑modell</FormLabel>
                  <FormControl>
                    <select {...field} className="flex h-9 w-full rounded-[3px] border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 aria-invalid:border-destructive aria-invalid:ring-destructive/40">
                      <option value="" disabled>Välj din modell</option>
                      {vehicleOptions.map((option, i) => <option key={option} value={option} data-index={i}>
                          {option}
                        </option>)}
                    </select>
                  </FormControl>
                  <FormDescription>Välj den Tesla‑modell du kör eller planerar att köra.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel>Telefonnummer (valfritt)</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+46 70 123 45 67" {...field} className="rounded-[3px]" />
                  </FormControl>
                  <FormDescription>Kan vara användbart för snabbare kontakt.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          {submitError && <div className="p-4 mb-6 bg-destructive/10 border border-destructive/20 rounded-[3px]">
              <p className="text-destructive text-sm font-medium">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto rounded-[3px] px-8">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Skickar...
              </> : 'Gå med i kollektivet'}
          </Button>
        </Form>
      </CardContent>
      <CardFooter className="border-t border-border/50 pt-6">
        <p className="text-[12px] font-sans text-muted-foreground">
          Genom att skicka in detta formulär godkänner du att RobotaxiNU sparar dina uppgifter för att kunna kontakta dig angående framtida försäkringserbjudanden. Dina uppgifter delas inte med tredje part utan ditt uttryckliga medgivande.
        </p>
      </CardFooter>
    </Card>;
}